/**
 * Phase 1.7 — secondary sub-sidebar.
 *
 * Used in route-group layouts (Settings is the only consumer in 1.7) to
 * render a 216px nav rail between the main sidebar and the page content.
 * Mirrors the visual treatment of the main Sidebar — same active rail,
 * same hover state — but renders nav items the parent passes as
 * { label, href, icon? }.
 */

import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SubSidebarItem {
  label: string;
  href: string;
  icon?: LucideIcon;
  description?: string;
}

export interface SubSidebarProps {
  title: string;
  items: SubSidebarItem[];
  className?: string;
}

export function SubSidebar({ title, items, className }: SubSidebarProps): JSX.Element {
  const location = useLocation();
  return (
    <aside
      className={cn(
        'shrink-0 border-r border-border bg-surface lg:w-subnav-w',
        'dark:bg-surface-dark dark:border-border',
        className,
      )}
      aria-label={title}
    >
      <div className="border-b border-border px-4 py-4 dark:border-border">
        <p className="font-mono text-2xs uppercase tracking-wider text-muted-foreground">{title}</p>
      </div>
      <ul className="space-y-0.5 p-2">
        {items.map((item) => {
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          const Icon = item.icon;
          return (
            <li key={item.href}>
              <Link
                to={item.href}
                className={cn(
                  'group relative flex h-9 items-center gap-2 rounded-md px-3 text-sm transition-colors',
                  active
                    ? 'bg-surface-raised font-medium text-charcoal dark:bg-surface-raised-dark dark:text-charcoal-dark'
                    : 'text-muted-foreground hover:bg-surface-raised hover:text-charcoal dark:hover:bg-surface-raised-dark dark:hover:text-charcoal-dark',
                )}
              >
                {active ? (
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-burnt-orange dark:bg-burnt-orange-dark"
                  />
                ) : null}
                {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
                <span className="truncate">{item.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
